import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Spin } from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { GenericPaginatedDocument } from '@/components/print/GenericPaginatedDocument';
import customerSatisfactionSurveyService from '@/services/customerSatisfactionSurvey.service';
import type { CustomerSatisfactionSurvey } from '@/constants/customer-satisfaction-survey';
import { useCompanyProfileSettings, useSystemSettings } from '@/hooks/useSettings';
import { useAuthStore } from '@/store/authStore';
import { setCompanyLetterheadFromSettings } from '@/print-engine/company-letterhead';
import {
  buildCustomerSatisfactionRegisterPrint,
  buildCustomerSatisfactionSurveyPrint,
} from '@/print-engine/adapters/customer-satisfaction-adapter';
import { usePrintDocumentTitle } from '@/utils/printDocumentTitle';

const PAGE_TAKE = 200;

async function listAll(
  filters: {
    status?: string;
    satisfaction_result?: string;
    search?: string;
    date_from?: string;
    date_to?: string;
  }
): Promise<{ rows: CustomerSatisfactionSurvey[]; total: number }> {
  const rows: CustomerSatisfactionSurvey[] = [];
  let total = 0;
  let skip = 0;
  do {
    const page = await customerSatisfactionSurveyService.list({ ...filters, skip, take: PAGE_TAKE });
    total = page.total;
    rows.push(...(page.rows ?? []));
    if (!page.rows?.length) break;
    skip += page.rows.length;
  } while (rows.length < total && skip < 5000);
  return { rows, total };
}

const CustomerSatisfactionPrintPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const companyId = useAuthStore((s) => s.user?.company_id);
  const { data: settings } = useSystemSettings();
  const { data: profile, isLoading: profileLoading } = useCompanyProfileSettings(companyId);
  const surveyPrint = Boolean(id);
  const printedAt = useMemo(() => new Date(), []);

  const filters = useMemo(
    () => ({
      status: searchParams.get('status') || undefined,
      satisfaction_result: searchParams.get('result') || undefined,
      search: searchParams.get('search') || undefined,
      date_from: searchParams.get('date_from') || undefined,
      date_to: searchParams.get('date_to') || undefined,
    }),
    [searchParams]
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<CustomerSatisfactionSurvey[]>([]);
  const [total, setTotal] = useState(0);
  const [survey, setSurvey] = useState<CustomerSatisfactionSurvey | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const run = surveyPrint
      ? customerSatisfactionSurveyService.getById(id!).then((row) => {
          if (cancelled) return;
          if (!row) {
            setError('Survey not found');
            return;
          }
          setSurvey(row);
        })
      : listAll(filters).then((page) => {
          if (cancelled) return;
          setRows(page.rows);
          setTotal(page.total);
        });
    run
      .catch((e: { response?: { data?: { message?: string } }; message?: string }) => {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Could not load the register');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, id, surveyPrint]);

  const brand = useMemo(() => {
    setCompanyLetterheadFromSettings(profile);
    const pickProfile = (key: string) => {
      const value = profile?.[key]?.value;
      return value == null ? '' : String(value).trim();
    };
    const list = settings?.filter((s) => s.category === 'company') ?? [];
    const pickSetting = (key: string) => list.find((s) => s.setting_key === key)?.setting_value?.trim() || '';
    return {
      legalName:
        pickProfile('company_legal_name') ||
        pickSetting('company_legal_name') ||
        pickSetting('company_name') ||
        pickProfile('company_name') ||
        'Company',
      trn: pickProfile('tax_registration_number') || pickProfile('vat_registration_number'),
    };
  }, [profile, settings]);

  const model = useMemo(() => {
    if (surveyPrint) {
      if (!survey) return null;
      return buildCustomerSatisfactionSurveyPrint({ survey, brand, printedAt });
    }
    return buildCustomerSatisfactionRegisterPrint({
      rows,
      total,
      filters: {
        search: filters.search,
        status: filters.status,
        result: filters.satisfaction_result,
        dateFrom: filters.date_from,
        dateTo: filters.date_to,
      },
      brand,
      printedAt,
    });
  }, [brand, filters, printedAt, rows, survey, surveyPrint, total]);

  usePrintDocumentTitle(model?.documentNumber);

  const back = () => {
    if (id) navigate(`/growth/customer-satisfaction/${id}`);
    else navigate('/growth/customer-satisfaction');
  };

  if (loading || profileLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !model) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" showIcon message={error || 'Could not prepare the print'} />
      </div>
    );
  }

  return <GenericPaginatedDocument model={model} onClose={back} />;
};

export default CustomerSatisfactionPrintPage;
